import React, { useState, useEffect } from 'react';
import { Dispatch } from 'redux';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';

import Header from '../../../../layout/HeaderSignup';
const Cover = dynamic(() => import('./Cover'));
const Property = dynamic(() => import('./Property'));
const Location = dynamic(() => import('./Location'));
const Budget = dynamic(() => import('./Budget'));
const BedBath = dynamic(() => import('./BedBath'));
const SquareFeet = dynamic(() => import('./SquareFeet'));
const Parking = dynamic(() => import('./Parking'));
const Features = dynamic(() => import('./Features'));
const HomeStyles = dynamic(() => import('./HomeStyles'));
const Timeframe = dynamic(() => import('./Timeframe'));
const Agent = dynamic(() => import('./Agent'));
const PreApproval = dynamic(() => import('./PreApproval'));

import { updateSignupProgress } from '../../../../../actions/auth';

export interface BasicInfoProps {
  signupProgress: any;
  goToNextPart: Function;
  dispatch: any;
}

const TOTAL_PAGES = 11;

export const BasicInfo: React.FC<BasicInfoProps> = ({
  signupProgress,
  goToNextPart,
  dispatch,
}) => {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState<number>(0);

  //Progress Update
  useEffect(() => {
    if (signupProgress.info.basics) {
      const currentPage = Math.round(
        (signupProgress.info.basics * TOTAL_PAGES) / 100,
      );
      setCurrentPage(currentPage);
    }
  }, []);

  useEffect(() => {
    if (currentPage >= 0) {
      const progress = parseInt(((currentPage / TOTAL_PAGES) * 100).toFixed(2));
      dispatch(
        updateSignupProgress({
          info: {
            ...signupProgress?.info,
            basics: progress,
          },
        }),
      );
    }
  }, [currentPage]);

  //Page Navigation
  const goToNextPage = () => {
    if (currentPage < TOTAL_PAGES) {
      setCurrentPage((part: number) => part + 1);
    }
  };
  const goToPreviousPage = () => {
    if (currentPage !== 0) {
      let page: string = '';
      switch (currentPage) {
        case 11:
          page = 'agent';
          break;
        case 10:
          page = 'timeframe';
          break;
        case 9:
          page = 'homestyles';
          break;
        case 8:
          page = 'features';
          break;
        case 7:
          page = 'parking';
          break;
        case 6:
          page = 'sqfeet';
          break;
        case 5:
          page = 'bedbath';
          break;
        case 4:
          page = 'budget';
          break;
        case 3:
          page = 'location';
          break;
        case 2:
          page = 'property';
          break;
        case 1:
          page = 'cover';
          break;
        default:
          page = 'cover';
          break;
      }
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            question: page,
          },
        },
        undefined,
        {
          shallow: true,
        },
      );
      setCurrentPage((part: number) => part - 1);
    } else {
      if (!router.pathname.includes('survey')) {
        router.push(
          {
            pathname: router.pathname,
            query: {
              stage: 'intro',
              question: 'knowledge',
            },
          },
          undefined,
          {
            shallow: true,
          },
        );
      } else {
        router.push('/your-preferences');
      }
    }
  };

  //Page Progress bar Logic
  const renderProgressBar = () => {
    let progressBar: any = null;
    const progress = signupProgress.info.basics;
    if (currentPage >= 1) {
      progressBar = (
        <div className="sign-up-progress-bar">
          <div className="progress-bar" style={{ width: `${progress}%` }}>
            &nbsp;
          </div>
        </div>
      );
    }
    return progressBar;
  };

  //Handle Pagination On Browser Navigation
  const handleBrowserNavigation = (page: string) => {
    if (page === 'cover') {
      setCurrentPage(0);
    } else if (page === 'property') {
      setCurrentPage(1);
    } else if (page === 'location') {
      setCurrentPage(2);
    } else if (page === 'budget') {
      setCurrentPage(3);
    } else if (page === 'bedbath') {
      setCurrentPage(4);
    } else if (page === 'sqfeet') {
      setCurrentPage(5);
    } else if (page === 'parking') {
      setCurrentPage(6);
    } else if (page === 'features') {
      setCurrentPage(7);
    } else if (page === 'homestyles') {
      setCurrentPage(8);
    } else if (page === 'timeframe') {
      setCurrentPage(9);
    } else if (page === 'agent') {
      setCurrentPage(10);
    } else if (page === 'preapproval') {
      setCurrentPage(11);
    }
  };

  //Basics pagination Logic
  const renderPage = () => {
    let page: any = null;
    let currentPage = router?.query?.question || 'cover';
    switch (currentPage) {
      case 'cover':
        page = () => (
          <Cover
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'property':
        page = () => (
          <Property
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'location':
        page = () => (
          <Location
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'budget':
        page = () => (
          <Budget
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'bedbath':
        page = () => (
          <BedBath
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'sqfeet':
        page = () => (
          <SquareFeet
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'parking':
        page = () => (
          <Parking
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'features':
        page = () => (
          <Features
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'homestyles':
        page = () => (
          <HomeStyles
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'timeframe':
        page = () => (
          <Timeframe
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'agent':
        page = () => (
          <Agent
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      case 'preapproval':
        page = () => (
          <PreApproval
            goToNextPart={goToNextPart}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
        break;
      default:
        page = () => (
          <Cover
            goToNextPage={goToNextPage}
            handleBrowserNavigation={handleBrowserNavigation}
          />
        );
    }
    return page();
  };

  //Page Header
  const renderHeader = () => {
    let header: any = null;
    header = <Header goBack={goToPreviousPage} />;
    return header;
  };

  return (
    <React.Fragment>
      {renderHeader()}
      {renderProgressBar()}
      {renderPage()}
    </React.Fragment>
  );
};

const mapStateToProps = (state: any) => ({
  signupProgress: state.auth.signupProgress,
});
const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(BasicInfo);
